export type ChatUiMessage = {
  key: string;
  id: number;
  providerIndex?: number;
  source: 'provider' | 'archive';
  text: string;
  type: 'sent' | 'received';
  name: string;
  time: string;
  dayLabel?: string;
  delivered: boolean;
  read: boolean;
  ts: number;
};

const normalizeSid = (sid: unknown): string | null => {
  if (typeof sid !== 'string') return null;
  const trimmed = sid.trim();
  return trimmed.length ? trimmed : null;
};

export const getProviderMessageKey = (message: any): string => {
  const state = message?.state || {};
  const sid = normalizeSid(message?.sid) || normalizeSid(state.sid);
  if (sid) return `twilio:${sid}`;

  const timestamp = state.timestamp ? new Date(state.timestamp).getTime() : '';
  return `provider:${String(state.author || '')}:${String(state.index ?? '')}:${String(timestamp)}`;
};

export const getArchivedMessageKey = (message: {
  id: number;
  twilioMessageSid?: string | null;
}): string => {
  const sid = normalizeSid(message.twilioMessageSid);
  return sid ? `twilio:${sid}` : `archive:${message.id}`;
};

/**
 * Combines provider and archived history into one exact-once chronology.
 * Provider copies win SID collisions because they carry current delivery/read state.
 */
export const mergeChatMessages = (...groups: ChatUiMessage[][]): ChatUiMessage[] => {
  const merged = new Map<string, ChatUiMessage>();

  groups.forEach((group) => {
    group.forEach((message) => {
      const existing = merged.get(message.key);
      if (!existing || message.source === 'provider' || existing.source !== 'provider') {
        merged.set(message.key, message);
      }
    });
  });

  return Array.from(merged.values()).sort((left, right) => {
    if (left.ts !== right.ts) return left.ts - right.ts;
    if (left.providerIndex !== undefined && right.providerIndex !== undefined) {
      return left.providerIndex - right.providerIndex;
    }
    return left.key.localeCompare(right.key);
  });
};
